"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { UserCheck, MapPin, TrendingUp, FileText, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
const features = [{
  iconName: "UserCheck",
  title: "Direct consultant access",
  description: "You work directly with me on every audit, roadmap, and campaign. No account managers, no ticketing systems, and no junior delegates handling your search footprint."
}, {
  iconName: "MapPin",
  title: "Bespoke Bedford search strategy",
  description: "Custom local keyword targeting, Google Business Profile optimisation, and local citation architecture designed specifically for the Bedford and Bedfordshire commercial market."
}, {
  iconName: "TrendingUp",
  title: "Revenue over vanity rankings",
  description: "I focus my work on real business outcomes: phone calls, qualified consultation inquiries, and paying customers, rather than empty impression spikes."
}, {
  iconName: "FileText",
  title: "Transparent and honest reporting",
  description: "Clear, plain-English progress tracking with actionable monthly reviews. You will always know exactly what I am working on and the exact results achieved."
}];
const pillars = [{
  title: "1-on-1 direct partnership",
  subtitle: "Direct access to Yassine Essaadi"
}, {
  title: "Tailored local roadmap",
  subtitle: "Built around your business goals"
}, {
  title: "Practical implementation",
  subtitle: "Hands-on technical & local fixes"
}, {
  title: "Clear deliverable tracking",
  subtitle: "Honest measurement every month"
}];
export default function BedfordConsultant() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const renderIcon = (name: string) => {
    switch (name) {
      case "UserCheck":
        return <UserCheck className="size-6 text-primary" />;
      case "MapPin":
        return <MapPin className="size-6 text-primary" />;
      case "TrendingUp":
        return <TrendingUp className="size-6 text-primary" />;
      case "FileText":
        return <FileText className="size-6 text-primary" />;
      default:
        return <CheckCircle2 className="size-6 text-primary" />;
    }
  };
  return <section id="finallybuiltforbedford" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="flex flex-col gap-16 lg:gap-20">
          {/* Header Block */}
          <div className="max-w-3xl">
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="secondary" className="px-3.5 py-1 text-xs font-medium tracking-wide uppercase bg-secondary text-secondary-foreground border border-border/50 rounded-full">
                <Sparkles className="size-3.5 mr-1.5 text-accent" />
                Bespoke local search
              </Badge>
            </motion.div>

            <motion.h2 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Finally, a local SEO consultant built for Bedford businesses
            </motion.h2>

            <motion.p variants={itemVariants} className="mt-6 text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed">
              When you hire me, you work directly with an experienced freelance SEO consultant. I do not run an agency, pass your campaigns to junior staff, or recycle standard templates. I build and execute targeted local strategies designed to bring qualified local customers straight to your business.
            </motion.p>
          </div>

          {/* Core Feature Grid */}
          <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {features.map((feature, i) => <motion.div key={feature.title} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
            y: -4
          }} className="h-full">
                <Card className="h-full bg-card border border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300 rounded-xl p-6 sm:p-8 flex flex-col justify-between">
                  <div>
                    <div className="size-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6">
                      {renderIcon(feature.iconName)}
                    </div>
                    <CardHeader className="p-0 mb-3">
                      <CardTitle className="font-serif text-xl font-semibold text-card-foreground">
                        {feature.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <p className="font-sans text-sm sm:text-base text-muted-foreground leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Qualitative Pillars Strip */}
          <motion.div variants={itemVariants} className="bg-card/50 border border-border rounded-2xl p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-border">
              {pillars.map((pillar, i) => <div key={pillar.title} data-index={i} className="flex items-start gap-3.5 pt-4 sm:pt-0 sm:px-4 first:pt-0 first:pl-0 last:pr-0">
                  <div className="mt-0.5 shrink-0">
                    <ShieldCheck className="size-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-serif font-semibold text-sm sm:text-base text-foreground">
                      {pillar.title}
                    </h3>
                    <p className="font-sans text-xs sm:text-sm text-muted-foreground mt-0.5">
                      {pillar.subtitle}
                    </p>
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Action Callout & Button */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4 border-t border-border">
            <div>
              <h3 className="font-serif text-xl font-bold text-foreground">
                Ready to improve your Bedford search visibility?
              </h3>
              <p className="font-sans text-sm sm:text-base text-muted-foreground mt-1">
                Let us discuss your market position and find out where your local search opportunities are.
              </p>
            </div>

            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg shadow-sm font-medium transition-[background-color,box-shadow] shrink-0">
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}